import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, fileUrl, avatarUrl, formatApiError } from "@/lib/api";
import { Search, BadgeCheck, Ban, ShieldCheck, Users, Crown, IndianRupee, Heart } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);

  useEffect(() => { loadStats(); loadUsers(); loadReports(); }, []);

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* ignore */ }
  };
  const loadUsers = async (search = "") => {
    try { const { data } = await api.get(`/admin/users${search ? `?q=${encodeURIComponent(search)}` : ""}`); setUsers(data.results || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadReports = async () => {
    try { const { data } = await api.get("/admin/reports"); setReports(data.results || []); } catch { /* ignore */ }
  };

  const verify = async (id) => { try { await api.post(`/admin/users/${id}/verify`); toast.success("Verified"); loadUsers(q); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const block = async (id) => { try { await api.post(`/admin/users/${id}/block`); toast.success("Blocked"); loadUsers(q); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unblock = async (id) => { try { await api.post(`/admin/users/${id}/unblock`); toast.success("Unblocked"); loadUsers(q); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const approvePhoto = async (id) => { try { await api.post(`/admin/users/${id}/photo/approve`); toast.success("Photo approved"); loadUsers(q); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <p className="overline">Operations</p>
        <h1 className="font-serif text-4xl text-brand-ink mt-2">Admin dashboard</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
            <StatCard icon={Users} label="Total users" value={stats.total_users} />
            <StatCard icon={BadgeCheck} label="Verified" value={stats.verified_users} />
            <StatCard icon={Crown} label="Premium / VIP" value={stats.premium_users} />
            <StatCard icon={Heart} label="Matches" value={stats.matches_accepted} />
            <StatCard icon={IndianRupee} label="Revenue (₹)" value={stats.revenue?.toLocaleString()} />
          </div>
        )}

        <div className="flex gap-3 mt-10 border-b border-brand-line">
          {[["users", "Users"], ["reports", "Reports"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-3 text-sm border-b-2 -mb-px ${tab === k ? "border-brand-maroon text-brand-maroon" : "border-transparent text-brand-inkLight hover:text-brand-ink"}`} data-testid={`admin-tab-${k}`}>{label}</button>
          ))}
        </div>

        {tab === "users" && (
          <>
            <div className="flex gap-3 mt-5">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-3 text-brand-inkMute" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email..." className="input-elegant pl-9" data-testid="admin-search-input" />
              </div>
              <button onClick={() => loadUsers(q)} className="btn-primary" data-testid="admin-search-btn">Search</button>
            </div>

            <div className="mt-6 card-elegant overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-brand-muted text-left text-xs uppercase tracking-wide text-brand-inkLight">
                  <tr>
                    <th className="p-3">User</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Plan</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-brand-line">
                      <td className="p-3 flex items-center gap-3">
                        <img src={avatarUrl(u, 80)} className="w-9 h-9 rounded-full object-cover" alt="" />
                        <div>
                          <p className="text-brand-ink">{u.name}</p>
                          <p className="text-xs text-brand-inkMute">{u.gender} · {u.age || "—"}y</p>
                        </div>
                      </td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3"><span className={`text-xs uppercase tracking-wide px-2 py-1 rounded-full ${u.plan === "vip" ? "bg-brand-gold/20 text-brand-goldDark" : u.plan === "premium" ? "bg-brand-maroon/10 text-brand-maroon" : "bg-brand-muted text-brand-inkLight"}`}>{u.plan}</span></td>
                      <td className="p-3">
                        {u.is_blocked ? <span className="text-xs text-brand-maroon">Blocked</span> :
                         u.is_verified ? <span className="text-xs text-green-700 flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Verified</span> : <span className="text-xs text-brand-inkMute">Pending</span>}
                      </td>
                      <td className="p-3 text-right space-x-2">
                        {!u.is_verified && <button onClick={() => verify(u.user_id)} className="text-xs text-brand-maroon hover:underline" data-testid={`admin-verify-${u.user_id}`}>Verify</button>}
                        {!u.is_photo_verified && u.photo_url && <button onClick={() => approvePhoto(u.user_id)} className="text-xs text-brand-gold hover:underline">Approve Photo</button>}
                        {u.is_blocked ? <button onClick={() => unblock(u.user_id)} className="text-xs text-green-700 hover:underline">Unblock</button>
                          : <button onClick={() => block(u.user_id)} className="text-xs text-brand-maroon hover:underline" data-testid={`admin-block-${u.user_id}`}>Block</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "reports" && (
          <div className="mt-6 card-elegant p-5">
            {reports.length === 0 ? <p className="text-sm text-brand-inkLight">No reports yet.</p> : (
              <ul className="space-y-3" data-testid="admin-reports-list">
                {reports.map((r) => (
                  <li key={r.id} className="border-b border-brand-line pb-3 last:border-0">
                    <p className="text-sm text-brand-ink"><strong>{r.from_user_id}</strong> reported <strong>{r.target_user_id}</strong></p>
                    <p className="text-xs text-brand-inkLight mt-1">{r.reason}</p>
                    <p className="text-[10px] text-brand-inkMute mt-1">{r.created_at}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-elegant p-5">
      <div className="flex items-center gap-2 text-brand-gold"><Icon className="h-4 w-4" /> <span className="overline">{label}</span></div>
      <p className="font-serif text-3xl text-brand-ink mt-2">{value ?? "—"}</p>
    </div>
  );
}
